import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import ShareButton from '@/components/ShareButton';
import { formatAmount } from '@/utils/formatAmount';
import type { Donation } from '@/hooks/useDonations';

interface LeaderboardProps {
  donations: Donation[];
  loading: boolean;
  highestAllTime: Donation | null;
  monthlyHighest: Donation | null;
  newestDonation: Donation | null;
}

const ITEMS_PER_LOAD = 10;

const Leaderboard = ({
  donations,
  loading,
  highestAllTime,
  monthlyHighest,
  newestDonation,
}: LeaderboardProps) => {
  const { t } = useTranslation();
  const [displayCount, setDisplayCount] = useState(ITEMS_PER_LOAD);
  const [selectedDonation, setSelectedDonation] = useState<Donation | null>(null);
  const loaderRef = useRef<HTMLDivElement>(null);
  
  const visibleDonations = donations.slice(0, displayCount);
  const hasMore = displayCount < donations.length;
  
  // Reset display count when donations change (e.g., new donation arrives)
  useEffect(() => {
    if (donations.length > 0 && displayCount > donations.length) {
      setDisplayCount(Math.max(ITEMS_PER_LOAD, donations.length));
    }
  }, [donations.length, displayCount]);
  
  // Intersection Observer for infinite scroll
  const loadMore = useCallback(() => {
    if (hasMore) {
      setDisplayCount((prev) => Math.min(prev + ITEMS_PER_LOAD, donations.length));
    }
  }, [hasMore, donations.length]);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );
    
    const currentRef = loaderRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }
    
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, loadMore]);

  const getBadges = (donation: Donation) => {
    const badges: React.ReactNode[] = [];
    
    if (highestAllTime && donation.id === highestAllTime.id) {
      badges.push(
        <Badge key="top" className="bg-destructive text-destructive-foreground border-none ml-2">
          {t('leaderboard.biggestIdiot')}
        </Badge>
      );
    }
    
    if (monthlyHighest && donation.id === monthlyHighest.id && donation.id !== highestAllTime?.id) {
      badges.push(
        <Badge key="monthly" className="bg-accent text-accent-foreground border-none ml-2">
          {t('leaderboard.mongoOfMonth')}
        </Badge>
      );
    }
    
    if (newestDonation && donation.id === newestDonation.id) {
      badges.push(
        <Badge key="new" className="bg-primary text-primary-foreground border-none ml-2">
          {t('leaderboard.fresh')}
        </Badge>
      );
    }
    
    return badges;
  };

  if (loading) {
    return (
      <section className="px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 terminal-glow text-center">
            {t('leaderboard.title')}
          </h2>
          <div className="terminal-border p-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 mb-2 bg-muted" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section 
      className="px-4 py-12" 
      aria-labelledby="leaderboard-title"
      itemScope 
      itemType="https://schema.org/ItemList"
    >
      <div className="max-w-4xl mx-auto">
        <h2 
          id="leaderboard-title" 
          className="text-3xl md:text-4xl font-bold mb-2 terminal-glow text-center"
          itemProp="name"
        >
          {t('leaderboard.title')}
        </h2>
        <meta itemProp="numberOfItems" content={String(donations.length)} />
        <p className="text-muted-foreground text-center mb-8 text-sm">
          {t('leaderboard.subtitle')}
        </p>

        <div className="terminal-border overflow-hidden">
          {donations.length === 0 ? (
            <div className="p-12 text-center">
              <pre className="text-muted-foreground text-sm md:text-base">
{`
  ┌──────────────────────────────────┐
  │                                  │
  │   ${t('leaderboard.nobodyYet').padEnd(32)}│
  │                                  │
  │      ${t('leaderboard.beFirst').padEnd(28)}│
  │                                  │
  └──────────────────────────────────┘
`}
              </pre>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary hover:bg-transparent">
                    <TableHead className="text-primary font-bold w-12">#</TableHead>
                    <TableHead className="text-primary font-bold">{t('leaderboard.name')}</TableHead>
                    <TableHead className="text-primary font-bold hidden md:table-cell">{t('leaderboard.message')}</TableHead>
                    <TableHead className="text-primary font-bold text-right">{t('leaderboard.amount')}</TableHead>
                    <TableHead className="text-primary font-bold w-12 text-center">{t('leaderboard.share')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                {visibleDonations.map((donation, index) => {
                    const rank = index + 1;
                    return (
                      <TableRow 
                        key={donation.id} 
                        className="border-b border-muted hover:bg-secondary/20 cursor-pointer md:cursor-default"
                        onClick={() => {
                          // On mobile, clicking the row opens the modal
                          if (window.innerWidth < 768 && donation.message) {
                            setSelectedDonation(donation);
                          }
                        }}
                      >
                        <TableCell className="font-bold text-lg">
                          {rank === 1 ? '👑' : rank}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-bold">{donation.display_name}</span>
                            {getBadges(donation)}
                            {/* Show truncated message on mobile */}
                            {donation.message && (
                              <span className="md:hidden text-xs text-muted-foreground truncate max-w-[200px] mt-1">
                                {donation.message}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-muted-foreground max-w-xs">
                          {donation.message ? (
                            <button
                              onClick={() => setSelectedDonation(donation)}
                              className="truncate block text-left w-full hover:text-foreground transition-colors cursor-pointer"
                            >
                              {donation.message}
                            </button>
                          ) : (
                            <span className="italic">// {t('leaderboard.noComment')}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-bold terminal-glow whitespace-nowrap">
                          {formatAmount(Number(donation.amount), donation.currency)}
                        </TableCell>
                        <TableCell className="text-center">
                          <ShareButton
                            displayName={donation.display_name}
                            amount={Number(donation.amount)}
                            rank={rank}
                            message={donation.message}
                            currency={donation.currency}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Message Modal */}
              <Dialog open={!!selectedDonation} onOpenChange={() => setSelectedDonation(null)}>
                <DialogContent className="bg-background border-2 border-primary sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="terminal-glow flex items-center gap-2">
                      {selectedDonation?.display_name}
                      <span className="text-destructive">
                        ({formatAmount(Number(selectedDonation?.amount || 0), selectedDonation?.currency)})
                      </span>
                    </DialogTitle>
                  </DialogHeader>
                  <div className="terminal-border p-4 mt-2">
                    <p className="text-foreground whitespace-pre-wrap">
                      "{selectedDonation?.message}"
                    </p>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Infinite scroll loader */}
              <div ref={loaderRef} className="p-4 text-center">
                {hasMore ? (
                  <span className="text-muted-foreground text-sm animate-pulse">
                    {t('leaderboard.loadingMore', { defaultValue: '// loading more...' })}
                  </span>
                ) : donations.length > ITEMS_PER_LOAD ? (
                  <span className="text-muted-foreground text-sm">
                    {t('leaderboard.endOfList', { defaultValue: '// end of list' })}
                  </span>
                ) : null}
              </div>
            </>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-4">
          {t('leaderboard.totalLegends', { count: donations.length, defaultValue: `${donations.length} legends have burned their money here.` })}
        </p>
      </div>
    </section>
  );
};

export default Leaderboard;
